import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { NetworkParticipationModal } from '@components/network/network-participation-modal/NetworkParticipationModal'
import { AdasBox } from '@components/wrapper-components'
import { Info, Lock, Pencil } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import { getCalibrationCenter } from '../../api/api'
import { NETWORK_PARTICIPATION_ITEMS, isNetworkItemAvailable } from '../../constants/networkParticipation'
import { Logger } from '../../logger'

// Rows surfaced on the card — the same live set as the matrix columns
// (Farmers is excluded via `showInTable` until it becomes a live agreement).
// Platform features render in their own section ahead of the networks.
const DISPLAY_ITEMS = NETWORK_PARTICIPATION_ITEMS.filter((item) => item.showInTable !== false)
const CARD_SECTIONS = [
  { title: 'Platform Features', items: DISPLAY_ITEMS.filter((item) => item.section === 'platform') },
  { title: 'Networks', items: DISPLAY_ITEMS.filter((item) => item.section === 'network') }
]

const StatusPill = ({ connected }) =>
  connected ? (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-[#DCFCE7] px-3 py-1 text-[12px] font-semibold text-[#16803F]'>
      <span className='h-1.5 w-1.5 rounded-full bg-[#16803F]' />
      Connected
    </span>
  ) : (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-3 py-1 text-[12px] font-semibold text-[#6B7280]'>
      <span className='h-1.5 w-1.5 rounded-full bg-[#94A3B8]' />
      Not connected
    </span>
  )

StatusPill.propTypes = { connected: PropTypes.bool }

/**
 * Read-only-by-default view of the CURRENT calibration center's platform features
 * and network participation, rendered as two card lists (Platform Features, then
 * Networks). Matches the locked Settings → Platform Features design.
 *
 * `canEdit` (defaults to admin status — CenterStore `isCenterAdmin` OR system
 * `is_admin`) swaps the "READ ONLY" badge + support banner for an Edit button that
 * opens the shared NetworkParticipationModal (navy header, toggle list,
 * Cancel / Save Changes) so admins can persist changes in place.
 */
export const NetworkParticipationCard = ({ canEdit: canEditProp }) => {
  const ccId = useCenterStore((store) => store.ccId)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const isCenterAdmin = useCenterStore((store) => store.isCenterAdmin)
  const isSystemAdmin = useUserStore((store) => !!store.currentUser?.is_admin)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const isAdmin = isCenterAdmin || isSystemAdmin
  const canEdit = canEditProp ?? isAdmin

  const [flags, setFlags] = useState(null)
  const [editOpen, setEditOpen] = useState(false)

  // Seed only the networks the backend still returns — a flag key omitted from
  // the response means the referral source is globally disabled, and its row
  // must not render at all.
  const seedFromCenter = useCallback((center) => {
    const next = {}
    DISPLAY_ITEMS.forEach((item) => {
      if (isNetworkItemAvailable(item, center)) {
        next[item.flagField] = !!center?.[item.flagField]
      }
    })
    setFlags(next)
  }, [])

  useEffect(() => {
    if (ccId == null) return undefined
    let active = true

    const load = async () => {
      // Instant paint from the cached center, then refresh the authoritative flags.
      if (centerDetail?.id === ccId) seedFromCenter(centerDetail)
      setLoading(true)
      try {
        const response = await getCalibrationCenter({ ccid: ccId })
        if (!active) return
        if (response?.status === 200) seedFromCenter(response.data)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'NetworkParticipationCard', method: 'getCalibrationCenter', ccId } })
      } finally {
        if (active) setLoading(false)
      }
    }

    load()
    return () => {
      active = false
    }
    // centerDetail is only used for the instant-paint seed; keying on ccId is enough.
  }, [ccId, seedFromCenter, setLoading])

  // Merge the modal's saved flag map so the list reflects the change immediately.
  const handleSaved = useCallback((updatedFlags) => {
    setFlags((prev) => ({ ...prev, ...updatedFlags }))
  }, [])

  return (
    <AdasBox className='w-full'>
      {/* Top control row: READ ONLY badge for shops, Edit button for admins. */}
      <AdasBox className='mb-4 flex items-center justify-between gap-3'>
        {canEdit ? (
          <span className='text-[13px] text-[#6B7280]'>Manage the platform features and networks this location is enrolled in.</span>
        ) : (
          <span className='inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500'>
            <Lock className='h-3.5 w-3.5' />
            Read only
          </span>
        )}
        {canEdit && ccId != null && (
          <button
            type='button'
            onClick={() => setEditOpen(true)}
            className='inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-3 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
          >
            <Pencil className='h-4 w-4' />
            Edit Participation
          </button>
        )}
      </AdasBox>

      {/* Support banner — shown to shops, who can't self-serve participation. */}
      {!canEdit && (
        <AdasBox className='mb-6 flex items-start gap-2.5 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3'>
          <Info className='mt-0.5 h-4 w-4 shrink-0 text-[#2563EB]' />
          <span className='text-[13px] leading-relaxed text-[#1E3A5F]'>
            Platform features and network participation are managed by ADAS Intelligence. To make changes, contact support.
          </span>
        </AdasBox>
      )}

      {ccId == null ? (
        <AdasBox className='rounded-xl border border-border bg-white px-5 py-10 text-center text-[14px] text-[#6B7280]'>
          Select a calibration center to view its platform features and network participation.
        </AdasBox>
      ) : (
        CARD_SECTIONS.map((section) => {
          // Until the flags are seeded show the full static list; afterwards drop
          // networks whose flag key the backend omitted (globally disabled).
          const items = flags ? section.items.filter((item) => item.flagField in flags) : section.items
          return (
            <AdasBox key={section.title} className='mb-6'>
              <AdasBox className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>{section.title}</AdasBox>
              <AdasBox className='divide-y divide-[#EBEDF0] overflow-hidden rounded-xl border border-border bg-white'>
                {items.map((item) => {
                  // State-aware subtitle: swap to the inactive copy once flags show the item is off.
                  const subtitle = item.subtitleInactive && flags && !flags[item.flagField] ? item.subtitleInactive : item.subtitle
                  return (
                    <AdasBox key={item.key} className='flex items-center justify-between gap-4 px-5 py-4'>
                      {/* span (not div) wrapper so the row stays the nearest block ancestor */}
                      <span className='min-w-0'>
                        <span className='block text-[15px] font-semibold text-[#0A1628]'>{item.name}</span>
                        {subtitle && <span className='mt-0.5 block text-[13px] text-[#6B7280]'>{subtitle}</span>}
                      </span>
                      <StatusPill connected={!!flags?.[item.flagField]} />
                    </AdasBox>
                  )
                })}
              </AdasBox>
            </AdasBox>
          )
        })
      )}

      {canEdit && (
        <NetworkParticipationModal
          open={editOpen}
          center={ccId != null ? { id: ccId, name: centerDetail?.name } : null}
          onClose={() => setEditOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </AdasBox>
  )
}

NetworkParticipationCard.propTypes = {
  // When omitted, defaults to the current user's admin status.
  canEdit: PropTypes.bool
}
